import { type ComponentType } from "react";
import { useTheme } from "../../contexts/theme-context";
import type { StepKey } from "../../components/step-progress/step-progress";
import { useAuth } from "../../contexts/auth/auth-context";
import { trackEvent } from "../../utils/analytics";
import { usePwaInstall } from "../../hooks/use-pwa-install";
import { isNative } from "../../platform";
import { NATIVE_APPS_LIVE } from "./pages/qr-code/qr-code";

/* ───────────────── types ───────────────── */
export interface OnboardingStepProps {
    goNext: () => void;
    goPrev: () => void;
    goTo: (index: number) => void;
    stepIndex: number;
    showLogo?: boolean;
}

/** Steps can expose flags that influence the shell */
export type StepComponent = ComponentType<OnboardingStepProps> & {
    background?: string; // wrapper background
    fill?: boolean; // auto-height instead of 100 vh
    progress?: false | StepKey; // progress-bar phase or hide
    smallLogo?: boolean; // use 14-px logo instead of 20-px
    showLogo?: boolean; // show or hide shell logo
    containerAlign?: string; // flex align-items for container
    containerJustify?: string; // flex justify-content for container
};

export interface RegistrationShellProps {
    steps: StepComponent[];
}

/* ───────────────── hook ───────────────── */
export const useRegistrationShell = (steps: StepComponent[]) => {
    const { isLight } = useTheme();
    const { currentStep, setCurrentStep } = useAuth();
    const { isStandalone } = usePwaInstall();

    // Phase 1 (web) ends at the QR code screen (index 4).
    // When native apps are live, clamp web browser users to the QR screen
    // so they must install the app for Phase 2.
    // When native apps are NOT live, allow all steps on web.
    const QR_SCREEN_INDEX = 4;
    const isAppContext = isStandalone || isNative();
    const shouldClampToWeb = NATIVE_APPS_LIVE && !isAppContext;
    const maxStep = shouldClampToWeb ? QR_SCREEN_INDEX : steps.length - 1;

    // Clamp currentStep to valid range (and to maxStep for web when native apps are live)
    const index = Math.max(0, Math.min(currentStep ?? 0, maxStep, steps.length - 1));

    /* navigation helpers (write back into context) */
    const goNext = () => {
        trackEvent('registration_step_completed', {
            step_index: index,
            step_name: steps[index]?.displayName ?? `step_${index}`,
        });
        setCurrentStep(Math.min(index + 1, steps.length - 1));
    };

    const goPrev = () => setCurrentStep(Math.max(index - 1, 0));

    const goTo = (i: number) =>
        setCurrentStep(Math.max(0, Math.min(i, steps.length - 1)));

    const Step = steps[index];
    const background = Step.background ?? "#1A1A1C";
    const fill = Step.fill ?? false;
    const progressKey = Step.progress;
    const smallLogo = Step.smallLogo ?? false;
    const showLogo = Step.showLogo ?? true;
    const containerAlign = Step.containerAlign ?? "center";
    const containerJustify = Step.containerJustify ?? "center";

    return {
        isLight,
        index,
        goNext,
        goPrev,
        goTo,
        Step,
        background,
        fill,
        progressKey,
        smallLogo,
        showLogo,
        containerAlign,
        containerJustify,
    };
};
